import { useCallback, useEffect, useState } from "react";
import {
	type Charity,
	centsToDollars,
	dollarsToCents,
	type FormData,
} from "../types";
import {
	type ValidationError,
	validateDonationForm,
	validateField,
} from "../utils/validation";

const initialFormData: FormData = {
	recurring: false,
	allocationType: "default",
	basicDonationAmountCents: 0,
	specificAllocationsCents: {},
	tipType: "percentage",
	tipSize: 10,
	tipAmountCents: 0,
	firstName: "",
	lastName: "",
	email: "",
	country: "Hong Kong",
	paymentMethod: "credit-card",
	subscribeToUpdates: true,
	howDidYouHearAboutUs: "",
};

export const useFormState = (charities: Charity[] = []) => {
	const [formData, setFormData] = useState<FormData>(() => {
		return {
			...initialFormData,
			specificAllocationsCents: {},
		};
	});

	const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
		[],
	);
	const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

	// Update specific allocations when charities data is loaded
	useEffect(() => {
		if (charities.length > 0) {
			const specificAllocationsCents: Record<string, number> = {};
			charities.forEach((charity) => {
				specificAllocationsCents[charity.id.toString()] = 0;
			});

			setFormData((prev) => ({
				...prev,
				specificAllocationsCents,
			}));
		}
	}, [charities]);

	const updateFormData = useCallback((updates: Partial<FormData>) => {
		setFormData((prev) => ({ ...prev, ...updates }));
	}, []);

	// Mark field as touched and validate it
	const touchField = useCallback((fieldName: keyof FormData) => {
		setTouchedFields((prev) => new Set([...prev, fieldName]));
	}, []);

	// Validate a single field when it changes
	const validateSingleField = useCallback(
		(fieldName: keyof FormData, value: FormData[keyof FormData]) => {
			const fieldError = validateField(fieldName, value);

			setValidationErrors((prev) => {
				// Remove existing errors for this field
				const filteredErrors = prev.filter((err) => err.field !== fieldName);

				// Add new error if validation failed
				if (fieldError) {
					return [...filteredErrors, fieldError];
				}

				return filteredErrors;
			});
		},
		[],
	);

	// Validate entire form
	const validateForm = useCallback(() => {
		const validation = validateDonationForm(formData);
		setValidationErrors(validation.errors);
		return validation;
	}, [formData]);

	// Get error message for a specific field
	const getFieldError = useCallback(
		(fieldName: string) => {
			const error = validationErrors.find((err) => err.field === fieldName);
			const isTouched = touchedFields.has(fieldName);
			return isTouched && error ? error.message : null;
		},
		[validationErrors, touchedFields],
	);

	// Check if field has error
	const hasFieldError = useCallback(
		(fieldName: string) => {
			const isTouched = touchedFields.has(fieldName);
			return (
				isTouched && validationErrors.some((err) => err.field === fieldName)
			);
		},
		[validationErrors, touchedFields],
	);

	// Auto-update tip amount when relevant values change
	useEffect(() => {
		const totalDonationCents =
			formData.allocationType === "specific"
				? Object.values(formData.specificAllocationsCents).reduce(
						(sum, amount) => sum + amount,
						0,
					)
				: formData.basicDonationAmountCents;

		let tipAmountCents = 0;
		if (formData.tipType === "percentage") {
			tipAmountCents = Math.round(
				totalDonationCents * (formData.tipSize / 100),
			);
		} else {
			tipAmountCents = dollarsToCents(formData.tipSize);
		}

		if (tipAmountCents !== formData.tipAmountCents) {
			setFormData((prev) => ({ ...prev, tipAmountCents }));
		}
	}, [
		formData.allocationType,
		formData.specificAllocationsCents,
		formData.basicDonationAmountCents,
		formData.tipType,
		formData.tipSize,
		formData.tipAmountCents,
	]);

	const setDonationFrequency = useCallback(
		(recurring: boolean) => {
			// Bank transfers cannot be recurring - auto-switch to credit card
			if (recurring && formData.paymentMethod === "bank-transfer") {
				updateFormData({ recurring, paymentMethod: "credit-card" });
			} else {
				updateFormData({ recurring });
			}
		},
		[updateFormData, formData.paymentMethod],
	);

	const setAllocationType = useCallback(
		(type: "default" | "specific") => {
			updateFormData({ allocationType: type });
		},
		[updateFormData],
	);

	const setBasicDonationAmount = useCallback(
		(amountDollars: number) => {
			const amountCents = dollarsToCents(amountDollars);
			updateFormData({ basicDonationAmountCents: amountCents });
		},
		[updateFormData],
	);

	const setCharityAllocation = useCallback(
		(charityId: string, amountDollars: number) => {
			const amountCents = dollarsToCents(amountDollars);
			setFormData((prev) => ({
				...prev,
				specificAllocationsCents: {
					...prev.specificAllocationsCents,
					[charityId]: amountCents,
				},
			}));
		},
		[],
	);

	const setTipValues = useCallback(
		(amount: number, type: "percentage" | "dollar") => {
			updateFormData({ tipSize: amount, tipType: type });
		},
		[updateFormData],
	);

	const getSpecificAllocationsTotalCents = useCallback(() => {
		return Object.values(formData.specificAllocationsCents).reduce(
			(sum, amount) => sum + amount,
			0,
		);
	}, [formData.specificAllocationsCents]);

	const getSpecificAllocationsTotalDollars = useCallback(() => {
		return centsToDollars(getSpecificAllocationsTotalCents());
	}, [getSpecificAllocationsTotalCents]);

	return {
		formData,
		updateFormData,
		setDonationFrequency,
		setAllocationType,
		setBasicDonationAmount,
		setCharityAllocation,
		setTipValues,
		getSpecificAllocationsTotalCents,
		getSpecificAllocationsTotalDollars,
		// Validation methods
		touchField,
		validateSingleField,
		validateForm,
		getFieldError,
		hasFieldError,
		validationErrors,
	};
};
